import { formatOtsDateTime } from "@ots-frontend/ots-data";
import { OtsDateTimePicker } from "../components/OtsDateTimePicker";
import { OtsFormColumns, OtsFormRow, OtsFormSection, useOtsFormDensity } from "../components/OtsForm";
import type { OtsSelectOption } from "../components/OtsSelect";
import { OtsTextArea, OtsTextInput } from "../components/OtsTextInput";
import { OtsPersonSelect, OtsTrailerSelect, OtsVehicleSelect } from "../select/OtsEntitySelects";
import { type OtsUnitOfMeasureOption, OtsUnitOfMeasureSelect } from "../select/OtsUnitOfMeasureSelect";
import type { ShiftFormNames } from "./shift-form";
import type { ShiftFormState } from "./useShiftFormState";

export type ShiftFormFieldsProps = {
  state: ShiftFormState;
  /**
   * Lists only this region's drivers, trucks, and trailers; without one, the app's current region. An edit passes
   * the shift's own region.
   */
  regionId?: string;
  /** The shift's driver and equipment names, so they still show when they are not on the region's lists. */
  currentNames?: ShiftFormNames;
  /** Fixed choices instead of the region's records, for stories and tests. */
  options?: { person?: OtsSelectOption[]; vehicle?: OtsSelectOption[]; trailer?: OtsSelectOption[] };
  odometerUnits?: OtsUnitOfMeasureOption[];
  /** Pre and post billable minutes; WaterGo shows them to admins only. */
  showBillableTimes?: boolean;
  /** When the driver's tablet ended the shift, shown read-only on an edit. */
  tabletEndDttm?: number;
  timeZone?: string;
};

const parseNumber = (text: string) => {
  const value = Number.parseFloat(text);
  return text.trim() === "" || Number.isNaN(value) ? undefined : value;
};

/**
 * The shift form's fields, shared by the shift page and the shift dialog: driver and equipment, the shift's
 * times, the pre-trip and post-trip inspections with their billable minutes, the odometer, and a comment.
 */
export const ShiftFormFields = ({
  state,
  regionId,
  options,
  odometerUnits = [],
  showBillableTimes = false,
  tabletEndDttm,
  timeZone,
  currentNames,
}: ShiftFormFieldsProps) => {
  const { values, errors, change, setName } = state;
  const saved = (id: string | undefined, name: string | undefined) => id && name ? [{ value: id, label: name }] : undefined;
  const size = useOtsFormDensity();
  const minutes = (field: "preBillableMinutes" | "postBillableMinutes", label: string, description: string) => (
    <OtsTextInput
      label={label}
      description={description}
      type="number"
      inputMode="numeric"
      min={0}
      step={1}
      size={size}
      value={values[field] ?? ""}
      error={errors[field]}
      onChange={(event) => change({ [field]: parseNumber(event.target.value) })}/>
  );

  const groups = (
    <>
      <OtsFormSection title="Driver and equipment">
        <OtsFormRow columns={3}>
          <OtsPersonSelect
            label="Driver"
            required
            regionId={regionId}
            options={options?.person}
            selectedOptions={saved(values.personId, currentNames?.personName)}
            size={size}
            value={values.personId}
            onChange={(personId) => change({ personId })}
            onEntityChange={(entity) => setName({ personName: entity?.displayName })}/>
          <OtsVehicleSelect
            label="Truck"
            regionId={regionId}
            options={options?.vehicle}
            selectedOptions={saved(values.vehicleId, currentNames?.vehicleName)}
            size={size}
            value={values.vehicleId}
            onChange={(vehicleId) => change({ vehicleId })}
            onEntityChange={(entity) => setName({ vehicleName: entity?.displayName })}/>
          <OtsTrailerSelect
            label="Trailer"
            required
            regionId={regionId}
            options={options?.trailer}
            selectedOptions={saved(values.trailerId, currentNames?.trailerName)}
            size={size}
            value={values.trailerId}
            onChange={(trailerId) => change({ trailerId })}
            onEntityChange={(entity) => setName({ trailerName: entity?.displayName })}/>
        </OtsFormRow>
        {errors.personId && <p role="alert" className="text-xs font-medium text-[var(--ots-error)]">{errors.personId}</p>}
        {errors.trailerId && <p role="alert" className="text-xs font-medium text-[var(--ots-error)]">{errors.trailerId}</p>}
      </OtsFormSection>

      <OtsFormSection title="Shift">
        <OtsFormRow columns={tabletEndDttm != null ? 3 : 2}>
          <OtsDateTimePicker showSeconds label="Start" required includeTime size={size} value={values.startDttm} error={errors.startDttm}
            onChange={(startDttm) => change({ startDttm })}/>
          <OtsDateTimePicker showSeconds label="End" description="Leave empty while the shift is running." includeTime clearable size={size}
            value={values.endDttm} error={errors.endDttm} min={values.startDttm} onChange={(endDttm) => change({ endDttm })}/>
          {tabletEndDttm != null && (
            <OtsTextInput label="Tablet end time" description="As the driver's tablet ended it." readOnly size={size}
              value={formatOtsDateTime(new Date(tabletEndDttm), "short", { timeZone })}/>
          )}
        </OtsFormRow>
      </OtsFormSection>

      <OtsFormSection title="Pre-trip">
        <OtsFormRow columns={2}>
          <OtsDateTimePicker showSeconds label="Pre-trip inspection" description="Before the shift starts." includeTime clearable size={size}
            value={values.preDttm} error={errors.preDttm} max={values.startDttm}
            onChange={(preDttm) => change({ preDttm })}/>
          {showBillableTimes && minutes("preBillableMinutes", "Pre billable time (min)", "Billed before the shift starts.")}
        </OtsFormRow>
      </OtsFormSection>

      <OtsFormSection title="Post-trip">
        <OtsFormRow columns={2}>
          <OtsDateTimePicker showSeconds label="Post-trip inspection" description="After the shift ends." includeTime clearable size={size}
            value={values.postDttm} error={errors.postDttm} min={values.endDttm}
            onChange={(postDttm) => change({ postDttm })}/>
          {showBillableTimes && minutes("postBillableMinutes", "Post billable time (min)", "Billed after the shift ends.")}
        </OtsFormRow>
      </OtsFormSection>

      <OtsFormSection title="Odometer">
        <OtsFormRow columns={3}>
          <OtsTextInput label="Start reading" type="number" inputMode="decimal" size={size} value={values.startOdometer ?? ""}
            onChange={(event) => change({ startOdometer: parseNumber(event.target.value) })}/>
          <OtsTextInput label="End reading" type="number" inputMode="decimal" size={size} value={values.endOdometer ?? ""}
            error={errors.endOdometer} onChange={(event) => change({ endOdometer: parseNumber(event.target.value) })}/>
          <OtsUnitOfMeasureSelect label="Unit" units={odometerUnits} size={size} value={values.odometerUom}
            onChange={(odometerUom) => change({ odometerUom })}/>
        </OtsFormRow>
      </OtsFormSection>

      <OtsFormSection title="Comment">
        <OtsTextArea label="Add a comment" description="Added to the shift's comments." rows={3} size={size}
          value={values.comment ?? ""} onChange={(event) => change({ comment: event.target.value })}/>
      </OtsFormSection>
    </>
  );

  // Each group is its own bordered section: side by side in pairs on a wide page, stacked in a dialog.
  return <OtsFormColumns>{groups}</OtsFormColumns>;
};
